import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import '../styles/AdminProductAdd.css';
import ButtonRegular from './ui/ButtonRegular';
import Toast from './Toast';

const AdminProductAdd = () => {
	const [name, setName] = useState('');
	const [internalCode, setInternalCode] = useState('');
	const [price, setPrice] = useState('');
	const [description, setDescription] = useState('');
	const [stock, setStock] = useState(0);

	// ======== POWIĄZANE PRODUKTY ==========
	const [relatedProductIds, setRelatedProductIds] = useState([]);
	const [tempRelatedProductId, setTempRelatedProductId] = useState('');
	const [allProducts, setAllProducts] = useState([]); // For selecting related products

	// ======== KATEGORIE (MULTIPLE) ==========
	const [selectedCategories, setSelectedCategories] = useState([]); // Array of {categoryId, categoryName, subCategory}
	const [tempCategory, setTempCategory] = useState(''); // For dropdown selection
	const [tempSubCategory, setTempSubCategory] = useState('');
	const [tempSubCategoriesForSelectedCat, setTempSubCategoriesForSelectedCat] = useState([]);

	// ======== KOLORY ==========  
	const [colorsList, setColorsList] = useState([]);
	const [selectedColors, setSelectedColors] = useState([]);
	const [isNewColor, setIsNewColor] = useState(false);
	const [newColorName, setNewColorName] = useState('');

	// ======== KATEGORIE ==========  
	const [categories, setCategories] = useState([]);
	const [isNewCategory, setIsNewCategory] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');

	const [isNewSubCategory, setIsNewSubCategory] = useState(false);
	const [newSubCategoryName, setNewSubCategoryName] = useState('');

	// ======== WYMIARY ==========  
	const [height, setHeight] = useState('');
	const [width, setWidth] = useState('');
	const [depth, setDepth] = useState('');
	const [thickness, setThickness] = useState('');
	const [length, setLength] = useState('');
	const [weight, setWeight] = useState('');

	// ======== ZDJĘCIA ==========  
	const [images, setImages] = useState([]);
	const [previewUrls, setPreviewUrls] = useState([]);

	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [modalImage, setModalImage] = useState(null);

	const fileInputRef = useRef(null);
	const [toast, setToast] = useState('');
	

	// ======================================
	// POBIERANIE KATEGORII + KOLORÓW
	// ======================================
	useEffect(() => {
		axios
			.get(`${process.env.REACT_APP_API_URL}/api/categories`)
			.then((res) => setCategories(res.data))
			.catch((err) => console.error('Błąd pobierania kategorii:', err));

		axios
			.get(`${process.env.REACT_APP_API_URL}/api/colors`)
			.then((res) => setColorsList(res.data))
			.catch((err) => console.error('Błąd pobierania kolorów:', err));

		axios
			.get(`${process.env.REACT_APP_API_URL}/api/products`)
			.then((res) => setAllProducts(res.data))
			.catch((err) => console.error('Błąd pobierania produktów:', err));
	}, []);

	// ======================================
	// TWORZENIE NOWEJ KATEGORII
	// ======================================
	const createCategory = async () => {
		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/categories`,
				{ name: newCategoryName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			const newCat = res.data.category;
			setCategories((prev) => [...prev, newCat]);
			return newCat._id;
		} catch (err) {
			console.error('Błąd tworzenia kategorii:', err);
			throw err;
		}
	};

	// ======================================
	// DODAWANIE KATEGORII DO PRODUKTU
	// ======================================
	const addCategoryToProduct = async () => {
		if (!tempCategory) {
			setError('Wybierz kategorię.');
			return;
		}

		// If adding a new category
		let categoryId = tempCategory;
		let categoryName = categories.find((c) => c._id === tempCategory)?.name || '';

		if (tempCategory === 'newCat') {
			if (!newCategoryName.trim()) {
				setError('Podaj nazwę nowej kategorii.');
				return;
			}
			categoryId = await createCategory();
			categoryName = newCategoryName;
			setNewCategoryName('');
		}

		// Create the category entry
		const newEntry = {
			categoryId,
			categoryName,
			subCategory: tempSubCategory || '',
		};

		// Check if this combination already exists
		if (selectedCategories.some((sc) => sc.categoryId === categoryId && sc.subCategory === tempSubCategory)) {
			setError('Ta kategoria + podkategoria już jest dodana.');
			return;
		}

		setSelectedCategories((prev) => [...prev, newEntry]);
		setTempCategory('');
		setTempSubCategory('');
		setTempSubCategoriesForSelectedCat([]);
		setError('');
	};

	// ======================================
	// USUWANIE KATEGORII Z PRODUKTU
	// ======================================
	const removeCategoryFromProduct = (categoryId, subCategory) => {
		setSelectedCategories((prev) =>
			prev.filter((sc) => !(sc.categoryId === categoryId && sc.subCategory === subCategory))
		);
	};

	// ======================================
	// TWORZENIE NOWEJ PODKATEGORII
	// ======================================
	const createSubCategory = async (catId, subName) => {
		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.patch(
				`${process.env.REACT_APP_API_URL}/api/categories/${catId}/subcategories`,
				{ name: subName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			return res.data.category.subcategories.map((s) => s.name);
		} catch (err) {
			console.error('Błąd tworzenia podkategorii:', err);
			throw err;
		}
	};

	// ======================================
	// TWORZENIE NOWEGO KOLORU
	// ======================================
	const createColor = async () => {
		const formattedName =
			newColorName.charAt(0).toUpperCase() + newColorName.slice(1).toLowerCase();

		if (colorsList.some((c) => c.name.toLowerCase() === formattedName.toLowerCase())) {
			setError('Taki kolor już istnieje!');
			return null;
		}

		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/colors`,
				{ name: formattedName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			const newColor = res.data.color;
			setColorsList((prev) => [...prev, newColor]);
			return newColor.name;
		} catch (err) {
			console.error('Błąd tworzenia koloru:', err);
			setError('Nie udało się dodać koloru');
			return null;
		}
	};

	const removeColor = (colorName) => {
		setSelectedColors((prev) =>
			prev.filter((c) => c !== colorName)
		);
	};

	// ======================================
	// POWIĄZANE PRODUKTY
	// ======================================
	const addRelatedProduct = () => {
		if (!tempRelatedProductId.trim()) {
			setError('Wybierz produkt.');
			return;
		}

		if (relatedProductIds.includes(tempRelatedProductId)) {
			setError('Ten produkt już jest dodany.');
			return;
		}

		setRelatedProductIds((prev) => [...prev, tempRelatedProductId]);
		setTempRelatedProductId('');
		setError('');
	};

	const removeRelatedProduct = (id) => {
		setRelatedProductIds((prev) => prev.filter((pid) => pid !== id));
	};

	// ======================================
	// DODAWANIE PRODUKTU
	// ======================================
	const handleSubmit = async (e) => {
		e.preventDefault();

		if (selectedCategories.length === 0) {
			setError('Dodaj przynajmniej jedną kategorię.');
			setToast('Brak dodanej kategorii.');
			return;
		}

		// Build categories array for the router (format: [{ category: "id", subCategory: "name" }])
		const categoriesForRouter = selectedCategories.map((sc) => ({
			category: sc.categoryId,
			subCategory: sc.subCategory || '',
		}));

		const formData = new FormData();
		formData.append('name', name);
		formData.append('internalCode', internalCode);
		formData.append('price', price);
		formData.append('description', description);
		formData.append('categories', JSON.stringify(categoriesForRouter));
		if (relatedProductIds.length > 0) formData.append('relatedProducts', JSON.stringify(relatedProductIds));
		formData.append('stock', stock);
		selectedColors.forEach((col) => formData.append('colors', col));
		if (height) formData.append('height', height);
		if (width) formData.append('width', width);
		if (depth) formData.append('depth', depth);
		if (thickness) formData.append('thickness', thickness);
		if (length) formData.append('length', length);
		if (weight !== '' && weight !== undefined && weight !== null) formData.append('weight', weight);
		images.forEach((file) => formData.append('images', file));

		try {
			const token = localStorage.getItem('authToken');
			await axios.post(`${process.env.REACT_APP_API_URL}/api/products`, formData, {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			});

			setSuccessMessage('Produkt został dodany!');
			setToast('Produkt został dodany!');
			setError('');
			// RESET
			setName('');
			setInternalCode('');
			setPrice('');
			setDescription('');
			setWeight('');
			setSelectedColors([]);
			setIsNewColor(false);
			setNewColorName('');
			setStock(0);
			setImages([]);
			setPreviewUrls([]);
			setNewCategoryName('');
			setIsNewCategory(false);
			setNewSubCategoryName('');
			setIsNewSubCategory(false);
			setSelectedCategories([]);
			setTempCategory('');
			setTempSubCategory('');
			setTempSubCategoriesForSelectedCat([]);
			setRelatedProductIds([]);
			setTempRelatedProductId('');
			if (fileInputRef.current) fileInputRef.current.value = '';
		} catch (err) {
			console.error('Błąd dodawania produktu:', err);
			setToast(`Nie udało się dodać produktu: ${err.message}`);
			setError('Wystąpił błąd podczas dodawania produktu.');
		}
	};

	// ======================================
	// OBŁUGA ZDJĘĆ
	// ======================================
	const handleImageChange = (e) => {
		const files = Array.from(e.target.files);
		setImages((prev) => [...prev, ...files]);
		setPreviewUrls((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
	};

	const removeImage = (index) => {
		URL.revokeObjectURL(previewUrls[index]);
		setImages(images.filter((_, i) => i !== index));
		setPreviewUrls(previewUrls.filter((_, i) => i !== index));
	};

	// Funkcja zmiany kolejności zdjęć
	const moveImage = (fromIndex, toIndex) => {
		if (fromIndex === toIndex) return;

		const updatedImages = [...images];
		const updatedPreviewUrls = [...previewUrls];

		const movedImage = updatedImages.splice(fromIndex, 1)[0];
		const movedPreviewUrl = updatedPreviewUrls.splice(fromIndex, 1)[0];

		updatedImages.splice(toIndex, 0, movedImage);
		updatedPreviewUrls.splice(toIndex, 0, movedPreviewUrl);

		setImages(updatedImages);
		setPreviewUrls(updatedPreviewUrls);
	};

	const openModal = (img) => setModalImage(img);
	const closeModal = () => setModalImage(null);

	// ======================================
	// RENDER
	// ======================================
	return (
		<div className="add-product">
			<h3>Dodaj nowy produkt</h3>
			{error && <p className="error-message">{error}</p>}
			{successMessage && <p className="success-message">{successMessage}</p>}

			<form onSubmit={handleSubmit}>
				{/* Nazwa */}
				<label>
					Nazwa:<span className="required">*</span>
				</label>
				<input value={name} onChange={(e) => setName(e.target.value)} required />


				{/* Kod */}
				<label>
					Kod wewnętrzny:<span className="required">*</span>
				</label>
				<input value={internalCode} onChange={(e) => setInternalCode(e.target.value)} required />

				{/* Cena */}
				<label>
					Cena:<span className="required">*</span>
				</label>

				<input
					type="number"
					value={price}
					step="0.01"
					min="0"
					onChange={(e) => setPrice(e.target.value)}
					onBlur={() => {
						if (price !== '') {
							setPrice(Number(price).toFixed(2));
						}
					}}
				/>

				{/* Opis */}
				<label>
					Opis:
				</label>
				<textarea value={description} onChange={(e) => setDescription(e.target.value)}/>

				{/* ============================================ KATEGORIA ============================================ */}
				<label>
					Kategorie:<span className="required">*</span>
				</label>
				{!isNewCategory && (
					<select
						value={tempCategory}
						onChange={(e) => {
							const val = e.target.value;
							if (val === 'newCat') {
								setIsNewCategory(true);
								setTempCategory('');
								setTempSubCategory('');
								setTempSubCategoriesForSelectedCat([]);
								return;
							}
							setTempCategory(val);
							const selectedCat = categories.find((c) => c._id === val);
							setTempSubCategoriesForSelectedCat(selectedCat?.subcategories?.map((s) => s.name) || []);
							setTempSubCategory('');
						}}
					>
						<option value="">-- wybierz kategorię --</option>
						{categories.map((cat) => (
							<option key={cat._id} value={cat._id}>
								{cat.name}
							</option>
						))}
						<option value="newCat">➕ Dodaj nową kategorię</option>
					</select>
				)}
				{isNewCategory && (
					<div className="new-cat-box">
						<input
							type="text"
							placeholder="Nowa kategoria"
							value={newCategoryName}
							onChange={(e) => setNewCategoryName(e.target.value)}
						/>
						<button
							type="button"
							onClick={async () => {
								if (!newCategoryName.trim()) return;
								const newCatId = await createCategory();
								setTempCategory(newCatId);
								setIsNewCategory(false);
								setNewCategoryName('');
							}}
						>
							Dodaj
						</button>
						<button
							type="button"
							onClick={() => {
								setIsNewCategory(false);
								setNewCategoryName('');
								setTempCategory('');
							}}
							className="cancel-btn"
						>
							Anuluj
						</button>
					</div>
				)}

				{/* ============================================ PODKATEGORIA ============================================ */}
				<label>
					Podkategoria:
				</label>
				{!tempCategory && !isNewCategory && (
					<select disabled>
						<option>Najpierw wybierz kategorię</option>
					</select>
				)}
				{isNewCategory && (
					<select disabled>
						<option>Podkategorie dostępne po utworzeniu kategorii</option>
					</select>
				)}
				{!isNewCategory && tempCategory && !isNewSubCategory && (
					<select
						value={tempSubCategory}
						onChange={(e) => {
							if (e.target.value === 'newSub') {
								setIsNewSubCategory(true);
								setTempSubCategory('');
							} else {
								setTempSubCategory(e.target.value);
							}
						}}
					>
						<option value="">-- wybierz podkategorię --</option>
						{tempSubCategoriesForSelectedCat.map((sc, i) => (
							<option key={i} value={sc}>
								{sc}
							</option>
						))}
						<option value="newSub">➕ Dodaj nową podkategorię</option>
					</select>
				)}
				{isNewSubCategory && (
					<div className="new-subcat-box">
						<input
							type="text"
							placeholder="Nowa podkategoria"
							value={newSubCategoryName}
							onChange={(e) => setNewSubCategoryName(e.target.value)}
						/>
						<button
							type="button"
							onClick={async () => {
								if (!newSubCategoryName.trim()) return;
								const updated = await createSubCategory(tempCategory, newSubCategoryName);
								setTempSubCategoriesForSelectedCat(updated);
								setTempSubCategory(newSubCategoryName);
								setIsNewSubCategory(false);
								setNewSubCategoryName('');
							}}
						>
							Dodaj
						</button>
						<button
							type="button"
							onClick={() => {
								setIsNewSubCategory(false);
								setNewSubCategoryName('');
							}}
							className="cancel-btn"
						>
							Anuluj
						</button>
					</div>
				)}

				{/* ============================================ DODANE KATEGORIE ============================================ */}
				{selectedCategories.length > 0 && (
					<div className="added-categories">
						<h4>Dodane kategorie:</h4>
						<ul>
							{selectedCategories.map((sc, idx) => (
								<li key={idx}>
									<strong>{sc.categoryName}</strong> {sc.subCategory && `/ ${sc.subCategory}`}
									<button
										type="button"
										onClick={() => removeCategoryFromProduct(sc.categoryId, sc.subCategory)}
										className="remove-category-btn"
									>
										✕
									</button>
								</li>
							))}
						</ul>
					</div>
				)}

				{/* ============================================ PRZYCISK DODAJ KATEGORIĘ ============================================ */}
				{tempCategory && (
					<button type="button" onClick={addCategoryToProduct} className="add-category-btn">
						➕ Dodaj tę kategorię do produktu
					</button>
				)}

				{/* ============================================ POWIĄZANE PRODUKTY ============================================ */}
				<label>
					Powiązane produkty:
				</label>
				<div className="related-products-input">
					<select
						value={tempRelatedProductId}
						onChange={(e) => setTempRelatedProductId(e.target.value)}
					>
						<option value="">-- wybierz produkt --</option>
						{allProducts
							.filter((p) => !relatedProductIds.includes(p._id))
							.map((p) => (
								<option key={p._id} value={p._id}>
									{p.name} | {p.internalCode}
								</option>
							))}
					</select>
					<button type="button" onClick={addRelatedProduct} className="add-related-btn">
						➕ Dodaj
					</button>
				</div>

				{relatedProductIds.length > 0 && (
					<div className="added-related-products">
						<h4>Dodane powiązane produkty:</h4>
						<ul>
							{relatedProductIds.map((id) => {
								const product = allProducts.find((p) => p._id === id);
								return (
									<li key={id} className="related-product-item">
										<img
											src={product?.images?.[0] || 'https://via.placeholder.com/50'}
											alt={product?.name}
											className="related-product-image"
										/>
										<div className="related-product-info">
											<strong>{product?.name}</strong>
											<small>{product?.internalCode}</small>
										</div>
										<button
											type="button"
											onClick={() => removeRelatedProduct(id)}
											className="remove-related-btn"
										>
											✕
										</button>
									</li>
								);
							})}
						</ul>
					</div>
				)}

				{/* ============================================ KOLORY ============================================ */}
				<label>
					Kolory:
				</label>
				<div className="selected-colors">
					{selectedColors.map((col) => (
						<span key={col} className="color-tag">
							{col}
							<button
								type="button"
								data-color={col}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									const colorToRemove = e.currentTarget.dataset.color;
									removeColor(colorToRemove);
								}}
							>
								×
							</button>
						</span>
					))}
				</div>
				{!isNewColor ? (
					<select
						onChange={(e) => {
							const val = e.target.value;
							if (val === 'newColor') {
								setIsNewColor(true);
								return;
							}
							if (!selectedColors.includes(val)) {
								setSelectedColors((prev) => [...prev, val]);
							}
						}}
					>
						<option value="">-- wybierz kolor --</option>
						{colorsList
							.filter((c) => !selectedColors.includes(c.name))
							.map((c) => (
								<option key={c._id} value={c.name}>
									{c.name}
								</option>
							))}
						<option value="newColor">➕ Dodaj nowy kolor</option>
					</select>
				) : (
					<div className="new-color-box">
						<input
							type="text"
							placeholder="Nowy kolor"
							value={newColorName}
							onChange={(e) => setNewColorName(e.target.value)}
						/>
						<button
							type="button"
							onClick={async () => {
								if (!newColorName.trim()) return;
								const newCol = await createColor();
								if (newCol) setSelectedColors((prev) => [...prev, newCol]);
								setIsNewColor(false);
								setNewColorName('');
							}}
						>
							Dodaj
						</button>
						<button
							type="button"
							onClick={() => {
								setIsNewColor(false);
								setNewColorName('');
							}}
							className="cancel-btn"
						>
							Anuluj
						</button>
					</div>
				)}

				{/* Ilość w magazynie */}
				<label>
					Ilość w magazynie:
				</label>
				<input
					type="number"
					value={stock}
					min="0"
					onChange={(e) => setStock(e.target.value)}
				/>

				{/* Wymiary */}
				<div className="dimensions">
					<label>
						Wysokość (cm):
						<input type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
					</label>
					<label>
						Szerokość (cm):
						<input type="number" value={width} onChange={(e) => setWidth(e.target.value)} />
					</label>
					<label>
						Głębokość (cm):
						<input type="number" value={depth} onChange={(e) => setDepth(e.target.value)} />
					</label>
					<label>
						Grubość (cm):
						<input type="number" value={thickness} onChange={(e) => setThickness(e.target.value)} />
					</label>
					<label>
						Długość (cm):
						<input type="number" value={length} onChange={(e) => setLength(e.target.value)} />
					</label>
					<label>
						Waga (kg):
						<input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} />
					</label>
				</div>

				{/* Zdjęcia */}
				<h4>Zdjęcia produktu</h4>
				<input type="file" multiple accept="image/*" onChange={handleImageChange} ref={fileInputRef} />

				{previewUrls.length > 0 && (
					<div className="preview-container">
						{previewUrls.map((url, i) => (
							<div key={i} className="image-preview">
								<img
									src={url}
									alt="preview"
									onClick={() => openModal(url)}
									style={{ cursor: 'pointer' }}
								/>
								<button
									type="button"
									onClick={() => removeImage(i)}
									className="delete-icon"
								>
									×
								</button>

								{/* Przycisk zmiany kolejności */}
								<div className="image-order-buttons">
									<button
										type="button"
										onClick={() => moveImage(i, i - 1)}
										disabled={i === 0}
									>
										↑
									</button>
									<button
										type="button"
										onClick={() => moveImage(i, i + 1)}
										disabled={i === previewUrls.length - 1}
									>
										↓
									</button>
								</div>
							</div>
						))}
					</div>
				)}

				<ButtonRegular type="submit">Dodaj produkt</ButtonRegular>
			</form>

			{modalImage && (
				<div className="modal" onClick={closeModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<img src={modalImage} alt="big" />
						<button className="close-btn" onClick={closeModal}>
							×
						</button>
					</div>
				</div>
			)}
			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
};

export default AdminProductAdd;
